"use client";

import { Toolbar, ToolbarButton } from "@mui/x-data-grid";
import { Button, Box, Portal, Backdrop } from "@mui/material";
import UpdateIcon from "@mui/icons-material/Update";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import { DeleteConfirm } from "./deleteConfirm";
import { EditConfirm } from "./editConfirm";
import { CreateConfirm } from "./createConfirm";
import { BookmarkV2Response } from "@/api/bookmarks";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import { useBookmarkActions } from "./hooks";
import { BookmarkActionsEnum } from "./hooks";

interface BookmarkActionsProps {
  onActionSuccess: () => void;
  selectedRows: BookmarkV2Response[];
}

export default function BookmarkActions({
  selectedRows, 
  onActionSuccess,
}: BookmarkActionsProps) {
  const hasSelection = selectedRows.length > 0;
  const { actionSelected, handleClick, handleDelete, handleEdit, handleCreate, exportBookmarks, setActionSelected } = useBookmarkActions({ selectedRows, onActionSuccess });

  return (
    <>
      <Toolbar>
        <Box className="flex gap-2 items-center">
          <ToolbarButton>
            <Button
              variant="outlined"
              startIcon={<CreateNewFolderIcon />}
              onClick={() => handleClick(BookmarkActionsEnum.CREATE)}
              className="text-sm normal-case disabled:opacity-50"
            >
                Create New Folder
              </Button>
          </ToolbarButton>
          <ToolbarButton>
            <Button
              variant="outlined"
              startIcon={<UpdateIcon />}
              onClick={() => handleClick(BookmarkActionsEnum.EDIT)}
              disabled={selectedRows.length !== 1 || selectedRows.some((row) => !row.is_owner)}
              className="text-sm normal-case disabled:opacity-50"
            >
              Edit Folder
            </Button>
          </ToolbarButton>

          <ToolbarButton>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportBookmarks}
              disabled={selectedRows.length !== 1}
              className="text-sm normal-case disabled:opacity-50"
            >
              Export Folder
            </Button>
          </ToolbarButton>

          <ToolbarButton>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => handleClick(BookmarkActionsEnum.DELETE)}
              disabled={!hasSelection || selectedRows.some((row) => !row.is_owner)}
              className="text-sm normal-case disabled:opacity-50"
            >
              Delete ({selectedRows.length})
            </Button>
          </ToolbarButton>
        </Box>
      </Toolbar>
      <Portal>
        <Backdrop
          open={actionSelected === "delete"}
          className="z-[1300] flex items-center justify-center"
          onClick={() => setActionSelected(BookmarkActionsEnum.NONE)}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg shadow-lg"
          >
            <DeleteConfirm
              title="Delete Bookmarks"
              message={`Are you sure you want to delete ${selectedRows.length} bookmark${selectedRows.length === 1 ? '' : 's'}?`}
              actionIcon={<DeleteIcon />}
              onConfirm={handleDelete}
              onCancel={() => setActionSelected(BookmarkActionsEnum.NONE)}
            />
          </Box>
        </Backdrop>
      </Portal>
      <Portal>
        <Backdrop
          open={actionSelected === "create"}
          onClick={() => setActionSelected(BookmarkActionsEnum.NONE)}
          className="z-[1300] flex items-center justify-center"
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg shadow-lg"
          >
            <CreateConfirm
              title="Create New Folder"
              message="Create a new folder for this folder."
              onConfirm={(title, isPrivate) => handleCreate(title, isPrivate)}
              onCancel={() => setActionSelected(BookmarkActionsEnum.NONE)}
              actionIcon={<CreateNewFolderIcon />}
              actionColor="primary"
            />
          </Box>
        </Backdrop>
      </Portal>
      <Portal>
        <Backdrop
          open={actionSelected === "edit"}
          className="z-[1300] flex items-center justify-center"
          onClick={() => setActionSelected(BookmarkActionsEnum.NONE)}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg shadow-lg"
          >
            <EditConfirm
              title="Edit Folder"
              message="Edit the title and visibility for this folder."
              actionIcon={<UpdateIcon />}
              initialTitle={selectedRows.length > 0 ? selectedRows[0].title || "" : ""}
              initialIsPrivate={selectedRows.length > 0 ? selectedRows[0].visible || true : true}
              onConfirm={(title, isPrivate) => handleEdit(title, isPrivate)}
              onCancel={() => setActionSelected(BookmarkActionsEnum.NONE)}
            />
          </Box>
        </Backdrop>
      </Portal>
    </>
  );
}

